import { PNG_DATA_URI_PREFIX } from 'constants/pdf.constants';
import { getApiErrorMessage } from 'helpers/apiErrors';
import axios from 'services/axios';

import { EPropCertificatesTypes } from './actionsTypes';
import {
    CertificateRenderKind,
    CertificateRenderState,
    CertificateThumbnailResult,
    CertificatesThunk,
    PropCertificate,
} from './interfaces';

const getCertificateIds = (certificates: PropCertificate[]): string[] => {
    return certificates.map((certificate) => certificate.certId).filter((certId): certId is string => Boolean(certId));
};

export const prop_certificates_getCertificates = (): CertificatesThunk<PropCertificate[]> => {
    return async (dispatch, getState) => {
        try {
            dispatch({ type: EPropCertificatesTypes.GET_CERTIFICATES_START });
            const response = await axios.CertificatesApi.certificatesApiControllerGetCertificates();
            const certificates = Array.isArray(response?.data?.certificates)
                ? (response.data.certificates as PropCertificate[])
                : [];
            const socialSharing = response?.data?.socialSharing;
            const isPayoutCertificationEnabled = response?.data?.isPayoutCertificationEnabled;
            const isChallengePassCertificationEnabled = response?.data?.isChallengePassCertificationEnabled;

            dispatch({
                type: EPropCertificatesTypes.GET_CERTIFICATES_SUCCESS,
                payload: {
                    certificates,
                    socialSharing,
                    isPayoutCertificationEnabled,
                    isChallengePassCertificationEnabled,
                },
            });

            const { renderByCertId } = getState().prop_certificates;
            const missingThumbnailCertIds = getCertificateIds(certificates).filter(
                (certId) => !renderByCertId[certId]?.thumbnailDataUrl
            );
            if (missingThumbnailCertIds.length) {
                void dispatch(prop_certificates_getThumbnails(missingThumbnailCertIds));
            }
            return certificates;
        } catch (error) {
            dispatch({
                type: EPropCertificatesTypes.GET_CERTIFICATES_FAIL,
                payload: { error: getApiErrorMessage(error, 'Failed to load certificates') },
            });
            return [];
        }
    };
};

export const prop_certificates_getThumbnails = (certIds: string[]): CertificatesThunk<void> => {
    return async (dispatch) => {
        try {
            dispatch({ type: EPropCertificatesTypes.GET_THUMBNAILS_START, payload: { certIds } });
            const response = await axios.CertificatesApi.certificatesApiControllerGetCertificatesThumbnails({
                certIds,
            });
            const items = Array.isArray(response?.data?.items) ? response.data.items : [];
            const thumbnails: CertificateThumbnailResult[] = items
                .filter((item): item is typeof item & { certId: string } => Boolean(item?.certId))
                .map((item) => ({
                    certId: item.certId,
                    thumbnailDataUrl: item.imageBase64 ? `${PNG_DATA_URI_PREFIX}${item.imageBase64}` : undefined,
                }));

            dispatch({
                type: EPropCertificatesTypes.GET_THUMBNAILS_SUCCESS,
                payload: { certIds, thumbnails },
            });
        } catch (error) {
            dispatch({
                type: EPropCertificatesTypes.GET_THUMBNAILS_FAIL,
                payload: { certIds, error: getApiErrorMessage(error, 'Failed to load certificate thumbnails') },
            });
        }
    };
};

export const prop_certificates_renderCertificate = (
    certId: string,
    kind: CertificateRenderKind
): CertificatesThunk<CertificateRenderState | undefined> => {
    return async (dispatch, getState) => {
        try {
            dispatch({ type: EPropCertificatesTypes.RENDER_CERTIFICATE_START, payload: { certId } });

            const certificate = getState().prop_certificates.certificates.find((item) => item.certId === certId);
            const templateVersion = certificate?.templateVersion ?? '';
            const response = await axios.CertificatesApi.certificatesApiControllerRenderCertificate(
                certId,
                kind,
                templateVersion
            );

            if (kind === 'pdf') {
                const pdfBase64 = response?.data?.pdfBase64;
                if (typeof pdfBase64 !== 'string' || !pdfBase64) {
                    throw new Error('Certificate PDF is empty');
                }
                dispatch({
                    type: EPropCertificatesTypes.RENDER_CERTIFICATE_SUCCESS,
                    payload: { certId, kind, pdfBase64 },
                });
            } else {
                const imageBase64 = response?.data?.imageBase64;
                if (typeof imageBase64 !== 'string' || !imageBase64) {
                    throw new Error('Certificate image is empty');
                }
                dispatch({
                    type: EPropCertificatesTypes.RENDER_CERTIFICATE_SUCCESS,
                    payload: { certId, kind, dataUrl: `${PNG_DATA_URI_PREFIX}${imageBase64}` },
                });
            }

            return getState().prop_certificates.renderByCertId[certId];
        } catch (error) {
            dispatch({
                type: EPropCertificatesTypes.RENDER_CERTIFICATE_FAIL,
                payload: { certId, error: getApiErrorMessage(error, 'Failed to render certificate') },
            });
            return undefined;
        }
    };
};

export const prop_certificates_getCertificateShareLink = (certId: string): CertificatesThunk<string | undefined> => {
    return async (dispatch, getState) => {
        const cachedShareUrl = getState().prop_certificates.shareLinkByCertId[certId];
        if (cachedShareUrl) {
            return cachedShareUrl;
        }

        try {
            const response = await axios.CertificatesApi.certificatesApiControllerGetCertificateShareLink(certId);
            const shareUrl = response?.data?.shareUrl;
            if (typeof shareUrl !== 'string' || !shareUrl) {
                return undefined;
            }

            dispatch({ type: EPropCertificatesTypes.GET_SHARE_LINK_SUCCESS, payload: { certId, shareUrl } });
            return shareUrl;
        } catch (error) {
            console.error('Failed to get certificate share link', error);
            return undefined;
        }
    };
};
